import { useEffect, useMemo, useState } from 'react'
import { Alert, Descriptions, Modal, Space, Spin, Tag, TreeSelect, Typography } from 'antd'
import type { TreeSelectProps } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useSubscriberCountUnder } from '@/shared/api/reference'
import { usePermissions } from '@/shared/auth/usePermissions'
import { ancestorChain, buildTree, collectSubtreeIds, type TreeOf } from '@/shared/lib/tree'
import { fetchAgentSubtree, moveAgent, type AgentNode } from './agentApi'
import { useAgentIndex } from './useAgentIndex'

/**
 * "Ildizga chiqarish" uchun sun'iy qiymat.
 *
 * `TreeSelect` `null` ni "tanlanmagan" deb tushunadi, ya'ni ildizga
 * chiqarishni bo'sh maydon bilan ifodalab bo'lmasdi: operator "hech nima
 * tanlamadim" va "ataylab mustaqil qilyapman" ni farqlay olmasdi.
 */
const ROOT_VALUE = '__root__'

/** `TreeSelect` tugunining tipi — antd'ning O'ZIDAN (`OrgNodeMoveModal` naqshi) */
type AgentTreeSelectNode = NonNullable<TreeSelectProps['treeData']>[number]

interface AgentMoveModalProps {
  open: boolean
  node: AgentNode
  onClose: () => void
}

/**
 * Agent/resellerni TUZILMAGA BIRIKTIRISH — panelning eng xavfli amali.
 *
 * <h2>⚠️ Nega alohida oyna va nega raqamlar tugmadan OLDIN</h2>
 * Ko'chirish bitta maydonni almashtirmaydi. Backend javadoc'i aniq
 * yozadi: «o'z tugunini begona shox ostiga ulash — butun shoxini o'sha
 * yerga OLIB KIRISH demak». Bitta so'rov:
 *   1. butun shoxning `path` ini qayta hisoblaydi;
 *   2. abonentlardagi nusxani (`subscriber.reseller_path`) kaskad bilan
 *      yangilaydi;
 *   3. ya'ni doirasi shox bilan cheklangan operatorlar KO'RADIGAN
 *      abonentlar to'plamini o'zgartiradi — hech qanday xatosiz.
 *
 * Operator bitta tugun ko'chiryapman deb o'ylab o'nlab tugunni
 * ko'chirmasligi uchun ost-tugunlar SONI oynaning eng tepasida,
 * ogohlantirish matnining ICHIDA turadi.
 *
 * <h2>⚠️ YOPILGAN TUGUN TANLAGICHDA QOLADI</h2>
 * ADR-033 «yopilganlar standart holda yashiriladi» qoidasining ochiq
 * istisnosi: tugunning HOZIRGI otasi yopilgan bo'lishi mumkin va uni
 * ro'yxatdan chiqarish «hozir kimning ostida» qatorini bo'sh
 * qoldirardi — ya'ni mavjud biriktirilma yo'qdek ko'rinardi. Holat teg
 * bilan ko'rsatiladi, tanlov esa to'silmaydi (`AgentSelect` bilan bir
 * xil qaror va bir xil sabab).
 *
 * <h2>⚠️ Panel — QULAYLIK, himoya emas</h2>
 * Sikl serverda ham to'siladi (`AgentServiceImpl.move` →
 * `AgentException.CycleDetected`) va bazadagi trigger bilan ham. Bu
 * yerdagi taqiq operatorga sababni OLDINDAN aytish uchun; serverning
 * rad javobi baribir oynaning ichida ko'rsatiladi.
 */
export function AgentMoveModal({ open, node, onClose }: AgentMoveModalProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const queryClient = useQueryClient()
  const [target, setTarget] = useState<string | null>(null)

  // Har ochilishda tanlov TOZALANADI: oyna bir tugun uchun ochilib,
  // yopilib, boshqasi uchun qayta ochilganda eski nishon qolib ketardi.
  useEffect(() => {
    if (open) setTarget(null)
  }, [open, node.id])

  /**
   * Nomzod otalar — BUTUN ko'rinadigan daraxt.
   *
   * ⚠️ `useAgentIndex` server doirasidan o'tgan ro'yxatni beradi va
   * kesh kaliti boshqa ekranlar bilan BIR XIL, ya'ni oyna ochilganda
   * odatda yangi so'rov ketmaydi. Bu yerda filtr TAKRORLANMAYDI —
   * takrorlangan doira ajrashganda natija xato emas, JIMGINA BO'SH
   * bo'lardi.
   */
  const { agents, loading, reliable } = useAgentIndex(open)

  /**
   * Ta'sirlanadigan TUGUNLAR — SERVERDAN.
   *
   * ⚠️ Mahalliy daraxtdan sanash mumkin edi, lekin u faqat ko'rinish
   * doirasidagi qismni biladi. «Nechta tugun ko'chadi» degan savolga
   * yarim javob berish — eng yomon variant, chunki u ishonchli
   * ko'rinadi.
   */
  const subtree = useQuery({
    queryKey: queryKeys.agents.subtree(node.id),
    queryFn: () => fetchAgentSubtree(node.id),
    enabled: open,
    staleTime: 30_000,
  })

  /**
   * Ta'sirlanadigan ABONENTLAR.
   *
   * ⚠️ `agent.party_node` — `platform.org_node` ustidagi va
   * `is_settlement_party` bo'yicha FILTRLANGAN moslik VIEW i, ya'ni bu
   * ro'yxatdagi HAR tugun hisob-kitob birligi va so'rov ular uchun rost
   * javob beradi (`useSubscriberCountUnder` javadoc'idagi «0 bo'lib
   * qoladi» tuzog'i bu ekranga tegmaydi).
   */
  const mayCount = can('subscriber.read')
  const subscribers = useSubscriberCountUnder(node.id, open && mayCount)

  const forbidden = useMemo(() => collectSubtreeIds(agents, node.id), [agents, node.id])

  /** Qidiruv uchun matn — sarlavha JSX bo'lgani uchun alohida xarita */
  const searchText = useMemo(
    () => new Map(agents.map((item) => [item.id, `${item.displayName} ${item.code}`.toLowerCase()])),
    [agents],
  )

  const treeData = useMemo<AgentTreeSelectNode[]>(() => {
    const convert = (item: TreeOf<AgentNode>): AgentTreeSelectNode => {
      // IKKI xil taqiq, IKKI xil sabab — operator qaysi biri ekanini
      // bilishi kerak, aks holda «nega bosilmayapti» degan savol
      // javobsiz qolardi.
      const self = item.id === node.id
      const cycle = !self && forbidden.has(item.id)
      const reason = self
        ? t('agents.move.reasonSelf')
        : cycle
          ? t('agents.move.reasonCycle')
          : null

      return {
        value: item.id,
        title: (
          <Space size={4}>
            <span>{item.displayName}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {item.code}
            </Typography.Text>
            {/* ⚠️ Yopilgan tugun ro'yxatda QOLADI — klass izohiga qarang */}
            {item.state !== 'ACTIVE' ? (
              <Tag color="default">{t(`agentState.${item.state}`)}</Tag>
            ) : null}
            {reason ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                ({reason})
              </Typography.Text>
            ) : null}
          </Space>
        ),
        disabled: self || cycle,
        children: item.children.map(convert),
      }
    }

    return [
      {
        value: ROOT_VALUE,
        // ⚠️ Ildizga chiqarish YO'LI OCHIQ QOLADI — egasi ildiz reseller
        // ishlatadi. Bu «otani o'chirish» emas, tugunni MUSTAQIL qilish.
        title: (
          <Space size={4}>
            <span>{t('agents.move.toRoot')}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('agents.move.toRootHint')}
            </Typography.Text>
          </Space>
        ),
      },
      ...buildTree(agents).map(convert),
    ]
  }, [agents, forbidden, node.id, t])

  const targetId = target === ROOT_VALUE ? null : target
  const unchanged = target !== null && targetId === node.parentId

  const currentChain = ancestorChain(agents, node.parentId)
  const nextChain = ancestorChain(agents, targetId)

  const move = useMutation({
    mutationFn: () => moveAgent(node.id, targetId),
    onSuccess: () => {
      // ⚠️ `agents.all` PREFIKS: ildizlar, daraxt, shox va ota-tugunning
      // bolalari — hammasi eskirdi.
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.all })
      // ⚠️ Abonentlar ham: ularning `reseller_path` i kaskad bilan
      // o'zgardi, ya'ni doiraga bog'liq har qanday ro'yxat eskirgan.
      // Buni unutish «ko'chirdim, lekin ro'yxat o'zgarmadi» degan
      // ziddiyatni ekranda qoldirardi.
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
      onClose()
    },
  })

  /**
   * Shox ichidagi tugunlar soni.
   *
   * ⚠️ `subtree` javobi tugunning O'ZINI ham qaytaradi, shuning uchun
   * ost-tugunlar uchun bittasi ayiriladi. Ogohlantirishda aynan shu son
   * turadi: «1 ta tugun ko'chadi» deyish operatorga hech narsa
   * aytmasdi, «ostidagi 12 tasi ham ko'chadi» esa aytadi.
   */
  const affectedNodes = subtree.data?.length ?? null
  const descendants = affectedNodes === null ? null : Math.max(affectedNodes - 1, 0)

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda
      u eski va begona bo'lardi */
  const close = () => {
    move.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('agents.move.title', { name: node.displayName })}
      onCancel={close}
      onOk={() => move.mutate()}
      confirmLoading={move.isPending}
      okText={t('agents.move.confirm')}
      okButtonProps={{ danger: true, disabled: target === null || unchanged }}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={680}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna
          ochiqligicha turadi va niqob ortidagi xabarni operator umuman
          ko'rmasdi */}
      {move.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(move.error, t)}
        />
      ) : null}

      {/*
        ⚠️ OGOHLANTIRISHDA OST-TUGUNLAR SONI BOR va u UCH XIL bo'ladi:
        hali noma'lum, nol, yoki aniq son. Noma'lumni «0» bilan
        almashtirish eng xavfli yolg'on bo'lardi — «faqat o'zi ko'chadi»
        deb o'qilardi.
      */}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('agents.move.warningTitle')}
        description={
          descendants === null
            ? t('agents.move.warningUnknown')
            : descendants === 0
              ? t('agents.move.warningLeaf')
              : t('agents.move.warningBranch', { count: descendants })
        }
      />

      <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('agents.move.affectedNodes')}>
          {subtree.isPending ? (
            <Spin size="small" />
          ) : subtree.isError ? (
            <Typography.Text type="warning">{t('agents.move.affectedUnknown')}</Typography.Text>
          ) : (
            <Typography.Text strong>
              {t('agents.move.affectedNodesValue', { count: affectedNodes ?? 0 })}
            </Typography.Text>
          )}
        </Descriptions.Item>

        <Descriptions.Item label={t('agents.move.affectedSubscribers')}>
          {/*
            UCH XIL JAVOB, va ularni bir-biriga aylantirish TAQIQLANADI:
              · aniq son     — so'rov yuborildi va u rost
              · "noma'lum"   — so'rov yiqildi
              · "huquq yo'q" — operator abonentlarni ko'ra olmaydi
            Ikkinchi va uchinchisini `0` bilan almashtirish jimgina
            yolg'on bo'lardi.
          */}
          {!mayCount ? (
            <Typography.Text type="secondary">
              {t('agents.move.subscribersNoPermission')}
            </Typography.Text>
          ) : subscribers.isLoading ? (
            <Spin size="small" />
          ) : subscribers.isError || subscribers.count === null ? (
            <Typography.Text type="warning">{t('agents.move.affectedUnknown')}</Typography.Text>
          ) : (
            <Typography.Text strong>
              {t('agents.move.affectedSubscribersValue', { count: subscribers.count })}
            </Typography.Text>
          )}
        </Descriptions.Item>

        <Descriptions.Item label={t('agents.move.currentParent')}>
          {currentChain.length === 0 ? (
            <Tag>{t('agents.move.toRoot')}</Tag>
          ) : (
            currentChain.map((item) => item.displayName).join(' › ')
          )}
        </Descriptions.Item>

        <Descriptions.Item label={t('agents.move.newParent')}>
          {target === null ? (
            <Typography.Text type="secondary">{t('agents.move.notSelected')}</Typography.Text>
          ) : nextChain.length === 0 ? (
            <Tag color="blue">{t('agents.move.toRoot')}</Tag>
          ) : (
            <Typography.Text strong>
              {nextChain.map((item) => item.displayName).join(' › ')}
            </Typography.Text>
          )}
        </Descriptions.Item>
      </Descriptions>

      <Typography.Text type="secondary">{t('agents.move.pickParent')}</Typography.Text>
      {/*
        ⚠️ `allowClear` ATAYLAB YO'Q. Tozalash `onChange(undefined)`
        beradi, undefined esa so'rovdan `newParentId` ni butunlay
        yo'qotardi va server uni ILDIZGA CHIQARISH deb o'qirdi.
        "Tanlovni bekor qildim" degan harakat jimgina ko'chirishga
        aylanardi. Ildizga chiqarish uchun ro'yxatning birinchi bandi
        bor va u ANIQ nomlangan.

        ⚠️ `treeNodeFilterProp="title"` yaramaydi: sarlavha JSX, matn
        emas. Qidiruv nom va kod ustidan alohida xarita bo'yicha ishlaydi
        — aks holda qidiruv maydoni bosilardi, lekin hech nima topmasdi.
      */}
      <TreeSelect
        value={target ?? undefined}
        onChange={(value: string | undefined) => setTarget(value ?? null)}
        treeData={treeData}
        treeDefaultExpandAll
        showSearch
        loading={loading}
        filterTreeNode={(input, treeNode) => {
          const value = String(treeNode.value ?? '')
          if (value === ROOT_VALUE) return true
          return (searchText.get(value) ?? '').includes(input.trim().toLowerCase())
        }}
        placeholder={t('agents.move.pickParentPlaceholder')}
        style={{ width: '100%', marginTop: 8 }}
      />

      {/* Bo'sh ro'yxatning sababi AYTILADI: yuklanmagani, huquq yo'qligi
          va "haqiqatan bo'sh" — uchtasi bir xil emas */}
      {reliable && agents.length === 0 ? (
        <Alert type="info" showIcon style={{ marginTop: 12 }} message={t('agents.move.noCandidates')} />
      ) : null}

      {unchanged ? (
        <Alert type="info" showIcon style={{ marginTop: 12 }} message={t('agents.move.unchanged')} />
      ) : null}
    </Modal>
  )
}
